import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { CountrySelect } from '../common';
import ChangeLog from './ChangeLog';
import useScrollLock from '../../hooks/useScrollLock';
import { processImage, isImageFile, validateFileSize, MAX_IMAGE_MB, MAX_CATALOGUE_PDF_MB, formatSizeLimit } from '../../utils/imageProcessor';
import BrandCataloguesManager from './BrandCataloguesManager';
import './Salon.css';

const AddEditBrandModal = ({ isOpen, onClose, salonId, brand, onBrandAdded, onBrandUpdated, mode, userRole }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    brand_name: '',
    description: '',
    website: '',
    instagram_link: '',
    tiktok_link: '',
    facebook_link: '',
    country: '',
    display_image: '',
    contact_person_name: '',
    contact_person_email: '',
    contact_person_phone: '',
    notes: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState('');
  const modalBodyRef = useRef(null);
  const cataloguesRef = useRef(null);
  const originalBrandRef = useRef(null);
  const [isChangeLogVisible, setIsChangeLogVisible] = useState(false);
  const shouldScrollToFirstErrorRef = useRef(false);

  // Lock body scroll when modal is open
  useScrollLock(isOpen);

  // Scroll to first error field when form is submitted with errors
  useEffect(() => {
    if (shouldScrollToFirstErrorRef.current && Object.keys(errors).length > 0 && modalBodyRef.current) {
      // Order of fields in the form (to find first error)
      const fieldOrder = ['brand_name'];
      
      // Find the first field with an error
      const firstErrorField = fieldOrder.find(field => errors[field]);
      
      if (firstErrorField) {
        // Small delay to ensure DOM is updated
        setTimeout(() => {
          const errorElement = document.getElementById(firstErrorField);
          if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Focus the field to make it even more obvious
            errorElement.focus();
          }
          // Reset the flag after scrolling
          shouldScrollToFirstErrorRef.current = false;
        }, 100);
      } else {
        // If no specific field error found, reset flag
        shouldScrollToFirstErrorRef.current = false;
      }
    } else if (error && modalBodyRef.current && !shouldScrollToFirstErrorRef.current) {
      // If there's a general error (not field-specific), scroll to top
      modalBodyRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [error, errors]);

  useEffect(() => {
    if (isOpen && mode === 'edit' && brand) {
      const brandData = {
        brand_name: brand.brand_name || '',
        description: brand.description || '',
        website: brand.website || '',
        instagram_link: brand.instagram_link || '',
        tiktok_link: brand.tiktok_link || '',
        facebook_link: brand.facebook_link || '',
        country: brand.country || '',
        display_image: brand.display_image || '',
        contact_person_name: brand.contact_person_name || '',
        contact_person_email: brand.contact_person_email || '',
        contact_person_phone: brand.contact_person_phone || '',
        notes: brand.notes || ''
      };
      setFormData(brandData);
      originalBrandRef.current = brandData;
      setPreviewUrl(brand.display_image || '');
      setIsChangeLogVisible(false);
    } else if (isOpen && mode === 'add') {
      setFormData({
        brand_name: '',
        description: '',
        website: '',
        instagram_link: '',
        tiktok_link: '',
        facebook_link: '',
        country: '',
        display_image: '',
        contact_person_name: '',
        contact_person_email: '',
        contact_person_phone: '',
        notes: ''
      });
      originalBrandRef.current = null;
      setPreviewUrl('');
      setIsChangeLogVisible(false);
    }
    setSelectedFile(null);
    setError('');
    setErrors({});
    setSuccess('');
    // Reset scroll flag when modal opens
    shouldScrollToFirstErrorRef.current = false;
  }, [isOpen, mode, brand]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Reset scroll flag when user starts typing (they're fixing errors)
    shouldScrollToFirstErrorRef.current = false;
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const getLocalizedFieldName = (fieldName) => {
    const fieldNameMap = {
      'brand_name': 'brandName',
      'description': 'description',
      'website': 'website',
      'instagram_link': 'instagramLink',
      'tiktok_link': 'tiktokLink',
      'facebook_link': 'facebookLink',
      'country': 'country',
      'display_image': 'displayImage',
      'contact_person_name': 'contactPersonName',
      'contact_person_email': 'contactPersonEmail',
      'contact_person_phone': 'contactPersonPhone',
      'notes': 'notes'
    };

    const translationKey = fieldNameMap[fieldName];
    if (translationKey) {
      return t(`brands.${translationKey}`);
    }

    return fieldName
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type
      if (!isImageFile(file)) {
        setError('Only image files (JPEG, JPG, PNG, GIF, WEBP) are allowed');
        return;
      }

      // Check file size (the original - it is resized before upload)
      if (!validateFileSize(file)) {
        setError(t('messages.error.fileSizeMustBeLessThan', '', { size: formatSizeLimit(MAX_IMAGE_MB) }));
        return;
      }

      try {
        // Process image (resize and compress)
        const processedFile = await processImage(file);
        
        setSelectedFile(processedFile);
        setError('');

        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewUrl(e.target.result);
        };
        reader.readAsDataURL(processedFile);
      } catch (error) {
        console.error('Error processing image:', error);
        setError(t('messages.error.failedToProcessImage'));
      }
    }
  };

  const removeImage = () => {
    setSelectedFile(null);
    setPreviewUrl('');
    setFormData(prev => ({
      ...prev,
      display_image: ''
    }));
  };

  const logBrandChanges = async (originalBrand, updatedData) => {
    if (!originalBrand || !brand?.id) {
      return;
    }

    const trackedFields = [
      'brand_name',
      'description',
      'website',
      'instagram_link',
      'tiktok_link',
      'facebook_link',
      'country',
      'display_image',
      'contact_person_name',
      'contact_person_email',
      'contact_person_phone',
      'notes'
    ];

    const sanitizeValue = (value) => {
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'number') return String(value);
      return String(value).trim();
    };

    const changes = trackedFields.reduce((acc, field) => {
      const previousValue = sanitizeValue(originalBrand[field]);
      const newValue = sanitizeValue(updatedData[field]);
      if (previousValue !== newValue) {
        acc.push({
          field_name: field,
          previous_value: previousValue || null,
          new_value: newValue || null
        });
      }
      return acc;
    }, []);

    if (changes.length === 0) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.error('logBrandChanges: No token found');
      return;
    }

    try {
      await Promise.all(changes.map(change =>
        axios.post(
          `/api/salons/${salonId}/change-logs`,
          {
            entity_type: 'brand',
            entity_id: brand.id,
            field_name: change.field_name,
            previous_value: change.previous_value,
            new_value: change.new_value,
            change_type: 'update',
            metadata: { brand_id: brand.id }
          },
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        ).catch(error => {
          console.error('Error logging brand change:', {
            field: change.field_name,
            error: error.response?.data || error.message,
            status: error.response?.status
          });
        })
      ));
    } catch (error) {
      console.error('logBrandChanges: Error logging changes', error);
    }
  };

  const handleToggleChangeLog = () => {
    if (!brand?.id) return;
    setIsChangeLogVisible(!isChangeLogVisible);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.brand_name || !formData.brand_name.trim()) {
      newErrors.brand_name = t('messages.error.brandNameRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!validateForm()) {
      // Set flag to scroll to first error on next render
      shouldScrollToFirstErrorRef.current = true;
      return;
    }

    if (mode === 'edit' && cataloguesRef.current) {
      const catalogueError = cataloguesRef.current.validate();
      if (catalogueError) {
        setError(catalogueError);
        return;
      }
    }
    
    setLoading(true);
    setError('');
    setErrors({});
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      const headers = { 
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'multipart/form-data'
      };

      // Create FormData for file upload
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      // Add file if selected
      if (selectedFile) {
        submitData.append('display_image', selectedFile);
      }

      if (mode === 'add') {
        await axios.post(`/api/salons/${salonId}/dress-brands`, submitData, { headers });
        setSuccess(t('messages.success.created'));
        setTimeout(() => {
          onBrandAdded();
        }, 1000);
      } else {
        const response = await axios.put(`/api/salons/${salonId}/dress-brands/${brand.id}`, submitData, { headers });
        if (response.status === 200) {
          await logBrandChanges(originalBrandRef.current || formData, formData);
          if (cataloguesRef.current) {
            try {
              await cataloguesRef.current.syncCatalogues();
            } catch (syncErr) {
              // 413 comes from nginx (an HTML page, no JSON error) when the request is over its size limit.
              setError(syncErr.response?.status === 413
                ? t('messages.error.fileSizeMustBeLessThan', '', { size: formatSizeLimit(MAX_CATALOGUE_PDF_MB) })
                : syncErr.response?.data?.error || t('brands.failedToSaveCatalogue'));
              setLoading(false);
              return;
            }
          }
        }
        setSuccess(t('messages.success.updated'));
        setTimeout(() => {
          onBrandUpdated();
        }, 1000);
      }
    } catch (error) {
      console.error('Error submitting brand:', error);
      
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError(mode === 'add' ? t('messages.error.failedToAddBrand') : t('messages.error.failedToUpdateBrand'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h3>{mode === 'add' ? t('brands.addBrand') : t('brands.editBrand')}</h3>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body" ref={modalBodyRef}>
          <form id="brand-form" onSubmit={handleSubmit} noValidate>
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="brand_name">{t('brands.brandName')} *</label>
              <input
                type="text"
                id="brand_name"
                name="brand_name"
                value={formData.brand_name}
                onChange={handleInputChange}
                className={errors.brand_name ? 'error' : ''}
                placeholder={t('brands.brandName')}
              />
              {errors.brand_name && <span className="error-message">{errors.brand_name}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="country">{t('brands.country')}</label>
              <CountrySelect
                value={formData.country}
                onChange={(value) => setFormData(prev => ({ ...prev, country: value }))}
                placeholder={t('brands.selectCountry')}
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="description">{t('common.description')}</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              placeholder={t('brands.brandDescription')}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="website">{t('brands.website')}</label>
            <input
              type="url"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleInputChange}
              placeholder="https://example.com"
            />
          </div>

          <div className="form-group">
            <label htmlFor="display_image">{t('brands.brandImage')}</label>
            <div className="image-upload-container">
              <input
                type="file"
                id="display_image"
                name="display_image"
                accept="image/*"
                onChange={handleFileChange}
                className="file-input"
              />
              <label htmlFor="display_image" className="file-input-label">
                <span className="upload-icon">📷</span>
                {t('accessories.imageOptional')}
              </label>
              {previewUrl && (
                <div className="image-preview">
                  <img src={previewUrl} alt="Preview" className="preview-image" />
                  <button type="button" onClick={removeImage} className="remove-image-btn">
                    ✕
                  </button>
                </div>
              )}
            </div>
          </div>

          <div className="form-section">
            <h4>{t('brands.socialMedia')}</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="instagram_link">Instagram</label>
                <input
                  type="url"
                  id="instagram_link"
                  name="instagram_link"
                  value={formData.instagram_link}
                  onChange={handleInputChange}
                  placeholder="https://instagram.com/brandname"
                />
              </div>
              <div className="form-group">
                <label htmlFor="tiktok_link">TikTok</label>
                <input
                  type="url"
                  id="tiktok_link"
                  name="tiktok_link"
                  value={formData.tiktok_link}
                  onChange={handleInputChange}
                  placeholder="https://tiktok.com/@brandname"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="facebook_link">Facebook</label>
              <input
                type="url"
                id="facebook_link"
                name="facebook_link"
                value={formData.facebook_link}
                onChange={handleInputChange}
                placeholder="https://facebook.com/brandname"
              />
            </div>
          </div>

          <div className="form-section">
            <h4>{t('brands.contactInfo')}</h4>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="contact_person_name">{t('brands.contactName')}</label>
                <input
                  type="text"
                  id="contact_person_name"
                  name="contact_person_name"
                  value={formData.contact_person_name}
                  onChange={handleInputChange}
                  placeholder={t('brands.contactName')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="contact_person_email">{t('brands.contactEmail')}</label>
                <input
                  type="email"
                  id="contact_person_email"
                  name="contact_person_email"
                  value={formData.contact_person_email}
                  onChange={handleInputChange}
                  placeholder="email@example.com"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="contact_person_phone">{t('brands.contactPhone')}</label>
              <input
                type="tel"
                id="contact_person_phone"
                name="contact_person_phone"
                value={formData.contact_person_phone}
                onChange={handleInputChange}
                placeholder="+1 234 567 8900"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="notes">{t('brands.additionalNotes')}</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              placeholder={t('brands.brandNotes')}
              rows="3"
            />
          </div>

        {mode === 'edit' && brand?.id && (
          <BrandCataloguesManager
            ref={cataloguesRef}
            salonId={salonId}
            brandId={brand.id}
            userRole={userRole}
          />
        )}

        {mode === 'edit' && brand && (
          <>
            <div className="form-section">
              <div style={{ textAlign: 'center', display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  className="sleek-button change-log"
                  onClick={handleToggleChangeLog}
                  disabled={loading}
                >
                  {isChangeLogVisible ? t('reservations.hideChangeLog') : t('reservations.viewChangeLog')}
                </button>
              </div>
            </div>

            <ChangeLog
              salonId={salonId}
              entityType="brand"
              entityId={brand.id}
              getLocalizedFieldName={getLocalizedFieldName}
              isVisible={isChangeLogVisible}
              onToggle={handleToggleChangeLog}
              scrollContainerRef={modalBodyRef}
              titleKey="reservations.changeLogTitle"
              namespace="reservations"
            />
          </>
        )}

        </form>
        </div>
        
        <div className="modal-footer">
          <button 
            type="button" 
            onClick={handleClose} 
            className="salon-button secondary"
            disabled={loading}
          >
            {t('common.cancel')}
          </button>
          <button 
            type="submit" 
            form="brand-form"
            className="salon-button primary"
            disabled={loading}
          >
            {loading ? `${t('common.save')}...` : (mode === 'add' ? t('brands.addBrand') : t('common.saveChanges'))}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddEditBrandModal;
